import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  IonActionSheet,
  IonButton,
  IonItem,
  IonList,
  IonIcon,
  IonLabel,
  IonPopover
} from '@ionic/angular/standalone';
import { ITag } from '../../_models/tag.model';
import type { ActionSheetButton } from '@ionic/core/dist/types/components';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'lib-insight-actions',
  standalone: true,
  imports: [
    CommonModule,
    IonActionSheet,
    IonButton,
    IonItem,
    IonList,
    IonIcon,
    IonLabel,
    IonPopover
  ],
  templateUrl: './insight-actions.component.html',
  styleUrls: ['./insight-actions.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'lib-insight-actions' }
})
export class InsightActionsComponent implements OnDestroy {
  @Input() extraActions: Array<{
    id: string;
    text: string;
    icon?: string;
    checked?: boolean;
    disabled?: boolean;
  }> = [];

  @ViewChild('actionsPopover') actionsPopover?: IonPopover;
  /** Unique id for the trigger button (useful for testing/aria) */
  @Input({ required: true }) uniqueIdentifier!: string;

  /** Whether to show the admin-only "View tag insight" item */
  @Input() isAdmin: boolean | undefined = false;

  /** Current tag for enabling the actions */
  @Input() tag: ITag | null = null;

  /**
   * If true, the actions menu can be shown even when `tag` is null.
   * Intended for non-tag insights (ex: event timeline / sequencing).
   */
  @Input() allowWithoutTag = false;

  /**
   * If true, hide the trend toggle action.
   * Useful when there is no `tag` (or trends do not apply).
   */
  @Input() hideTrendAction = false;

  /**
   * If true, always show the ellipsis menu even if only a single action is available.
   * Useful for keeping a consistent UI ("ellipsis -> View insight").
   */
  @Input() forceMenu = false;

  /** Whether the tag is already in the trend bucket */
  @Input() inBucket = false;

  /** Fired when user clicks "View tag insight" */
  @Output() viewTagInsight = new EventEmitter<void>();

  /** Fired when user toggles trend bucket */
  @Output() toggleTrend = new EventEmitter<void>();

  /** Fired when user clicks a custom action */
  @Output() extraAction = new EventEmitter<string>();

  /** Controls the modal visibility */
  isModalOpen = false;
  usePopoverActions = false;
  actionsEvent?: Event;
  actionsButtons: ActionSheetButton[] = [];

  trackByExtraAction = (_: number, a: { id: string }) => a.id;

  get visibleActionCount(): number {
    let count = 0;
    if (!this.hideTrendAction && this.tag) count += 1; // Toggle trend
    if (this.isAdmin) count += 1; // View tag insight
    count += (this.extraActions ?? []).filter(a => !a.disabled).length;
    return count;
  }

  get shouldCollapseToSingleAction(): boolean {
    return this.visibleActionCount === 1 && !this.forceMenu;
  }

  get trendActionLabel(): string {
    return this.inBucket ? 'Remove from Trends' : 'Add to Trends';
  }

  private isMobileDimensions(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(max-width: 720px)')?.matches ?? false;
  }

  private computeUsePopoverActions(): boolean {
    if (Capacitor.isNativePlatform()) return false;
    return !this.isMobileDimensions();
  }

  private buildActionsButtons(): ActionSheetButton[] {
    const buttons: ActionSheetButton[] = [];
    if (this.isAdmin) {
      buttons.push({
        text: 'View tag insight',
        icon: 'eye-outline',
        handler: () => void this.onViewInsight()
      });
    }

    for (const a of this.extraActions ?? []) {
      if (a.disabled) continue;
      const text = a.checked ? `✓ ${a.text}` : a.text;
      buttons.push({
        text,
        icon: a.icon ?? 'options-outline',
        handler: () => void this.onExtraAction(a.id)
      });
    }

    if (!this.hideTrendAction && this.tag) {
      buttons.push({
        text: this.trendActionLabel,
        icon: this.inBucket ? 'remove-circle-outline' : 'add-outline',
        handler: () => void this.onToggleTrend()
      });
    }

    buttons.push({ text: 'Cancel', role: 'cancel' });
    return buttons;
  }

  ngOnDestroy(): void {
    void this.actionsPopover?.dismiss();
  }

  openActions(event: Event): void {
    event.stopPropagation();
    this.usePopoverActions = this.computeUsePopoverActions();
    this.actionsEvent = event;
    this.actionsButtons = this.buildActionsButtons();
    this.isModalOpen = true;
  }

  async closeActions(event?: Event, fromDismiss = false): Promise<void> {
    event?.stopPropagation();
    this.isModalOpen = false;
    if (fromDismiss) {
      this.actionsEvent = undefined;
    } else {
      try {
        await this.actionsPopover?.dismiss();
      } catch {}
    }
  }

  async onViewInsight(event?: Event): Promise<void> {
    event?.stopPropagation();
    await this.closeActions();
    this.viewTagInsight.emit();
  }

  async onToggleTrend(event?: Event): Promise<void> {
    event?.stopPropagation();
    if (!this.tag) return;
    await this.closeActions();
    this.toggleTrend.emit();
  }

  async onExtraAction(id: string, event?: Event): Promise<void> {
    event?.stopPropagation();
    await this.closeActions();
    this.extraAction.emit(id);
  }
}
